import { memo, useCallback, useEffect, useRef, useState } from "react";
import { useTheme } from "@mui/material";
import { Alert, Box, Button, Checkbox } from "@/shared/ui";
import { useAppDispatch } from "@/shared/hooks/store";
import SignatureCanvas from "react-signature-canvas";
import useDeviceType from "@/shared/hooks/useDeviceType";
import dayJS from '@/shared/helpers/dayjs';
import { useFormikWrapper } from "@/shared/hooks/formikWrapper";

import SignHereIcon from '@/assets/icons/x-icon-green.svg';
import HippacIcon from '@/assets/images/onboarding/hipaa.png';
import EncryptionIcon from '@/assets/images/onboarding/encryption.png';
import { usePrevious } from "@/shared/hooks/previous";
import { useScrollToFirstError } from "../useScrollToFirstError";
import { ReviewCompleteForm } from "../../../../../../shared/components/payment-form/constants";

interface IProps {
    completeLoading: boolean;
    onSubmit?(): void;
    formikControl: ReturnType<typeof useFormikWrapper<ReviewCompleteForm>>;
};

const ReviewSubmit = ({ completeLoading, onSubmit, formikControl }: IProps) => {
    const theme = useTheme();
    const dispatch = useAppDispatch();
    const [canvasWidth, setCanvasWidth] = useState(500); // Default width
    const sigCanvasRef = useRef(null);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const scrollToFirstError = useScrollToFirstError();

    const { deviceType } = useDeviceType();
    const isDesktop = deviceType === 'desktop';

    const prevAcceptSubmitting = usePrevious(formikControl.values.acceptSubmitting);

    // useEffect(() => {
    //     if (ref.current) dispatch(setFixedBlock(ref.current));
    //     return () => {
    //         dispatch(setFixedBlock(undefined));
    //     };
    // }, [ref, dispatch]);

    const trim = async () => {
        console.log(sigCanvasRef?.current, 'asdfasdfasdfasdf');
        const base64EncodedImage = sigCanvasRef?.current?.toDataURL('image/png');
        formikControl.setFieldValue('base64EncodedImage', base64EncodedImage);
    }

    const clear = useCallback(() => {
        sigCanvasRef?.current?.clear();
        formikControl.setFieldValue('base64EncodedImage', '');
        formikControl.setFieldTouched('base64EncodedImage', false);
        formikControl.setFieldError('base64EncodedImage', '');
    }, [])

    useEffect(() => {
        // Set canvas width based on parent div
        const updateCanvasSize = () => {
            if (containerRef.current) {
                containerRef?.current?.offsetWidth && setCanvasWidth(containerRef.current.offsetWidth);
            }
        };

        updateCanvasSize(); // Initial set
        window.addEventListener("resize", updateCanvasSize); // Update on resize

        return () => window.removeEventListener("resize", updateCanvasSize);
    }, []);

    useEffect(() => {
        prevAcceptSubmitting !== undefined && clear();
    }, [prevAcceptSubmitting, formikControl.values.acceptSubmitting]);

    const acceptCheckboxTouchedAndError = formikControl.touched.acceptSubmitting && Boolean(formikControl.errors.acceptSubmitting);
    const sigTouchedAndError = formikControl.touched.base64EncodedImage && Boolean(formikControl.errors.base64EncodedImage);

    return (
        <Box
            bgWhite
            data-testid="review-submit-block-container"
            sx={{
                width: '100%',
                justifyContent: 'center',
                display: 'flex',
                flexDirection: 'row',
                boxShadow: '0px 4px 7.1px 0px #0000000F',
                borderRadius: '20px',
                height: 'max-content',
                padding: '30px !important',
                [theme.breakpoints.down('sm')]: {
                    padding: '15px 20px 30px 20px !important',
                },
            }}
            className="!4xl:w-[640px] mb-4 lg:mb-0"
        >

            <div className='flex flex-col justify-center gap-[0.9375rem] items-center max-w-[700px] w-full'>
                {acceptCheckboxTouchedAndError && (
                    <Alert boldLabel={false} label="You must accept terms before signing & submitting." />
                )}
                <Checkbox
                    testId="review-terms-accept-checkbox"
                    label={
                        <span className='relative -top-1 font-atkinsonHyperlegible text-[16px] leading-[140%]'>
                            <span className="block sm-max:leading-5">
                                By signing, I acknowledge that I am switching my pharmacy to AmeriPharma MedBox and authorize AmeriPharma MedBox to initiate the transfer of my prescriptions. I understand that my electronic signature is legally binding.
                            </span>
                            <span className='block mt-2'>
                                I have read and agree to the <a href="https://medbox.com/terms-of-use/" target="_blank" rel="noopener noreferrer">
                                    <span style={{ fontWeight: '400' }} className="font-atkinsonBold text-bold text-primary underline leading-[130%]">Terms of Use</span>
                                </a>.
                            </span>
                        </span>
                    }
                    className={`revew__checkbox !items-start !gap-[13px] border p-[1.25rem] sm:p-[1.625rem] rounded-2xl ${acceptCheckboxTouchedAndError ? 'border-red-500' : formikControl.values.acceptSubmitting ? 'border-primary-400' : 'border-gray-300'
                        }`}
                    required
                    name="acceptSubmitting"
                    error={acceptCheckboxTouchedAndError}
                    checked={formikControl.values.acceptSubmitting}
                    onChange={formikControl.handleChange}
                    labelClassName="font-atkinsonHyperlegible !text-[17px] text-[#000]"
                />
                {formikControl.values.acceptSubmitting && <div className="flex w-full flex-col justify-start items-start font-atkinsonHyperlegible">
                    <span className="block text-20 sm:text-30">Draw Signature</span>
                    <span className='text-14 sm:text-18'>{`${isDesktop ? 'Use your mouse to sign.' : 'Use your finger to sign.'}`}</span>
                </div>}

                <div className={`flex-1 ${formikControl.values.acceptSubmitting ? 'bg-grey-100' : 'bg-grey-110'} bg-opacity-60 p-2 rounded-md relative border border-1 ${sigTouchedAndError ? 'border-2 border-red-500' : 'border-gray-110'} w-full`}>
                    {!formikControl.values.acceptSubmitting && (
                        <div className="absolute inset-0 bg-grey-120 z-[99] pointer-events-auto rounded-md"></div>
                    )}
                    <div
                        className="h-[170px] w-full overflow-hidden rounded-md"
                    >
                        <div className='flex items-center justify-center'>
                            <SignatureCanvas
                                clearOnResize={false}
                                onEnd={trim}
                                ref={sigCanvasRef}
                                canvasProps={{
                                    style: { width: canvasWidth, height: '170px' },
                                }}
                            />
                        </div>
                        <div className={`absolute bottom-1/3 left-[2px] flex items-end w-full gap-2 max-w-[700px] px-2 ${formikControl.values.acceptSubmitting ? '' : 'opacity-40'}`}>
                            <img className="h-[10px] w-[10px] sm:h-[13px] sm:w-[13px]" src={SignHereIcon} alt="Sign Here" />
                            <div ref={containerRef} className="flex-1 border-b border-[#003D33]"></div>
                            <div className='h-[14px] w-[14px] sm:h-[10px] sm:w-[10px]' />
                        </div>
                    </div>
                </div>
                {formikControl.values.acceptSubmitting && <div className="flex flex-row justify-between items-center w-full">
                    <span className="font-atkinson text-black text-15 sm:text-16">{dayJS().format('MMM D, YYYY')}</span>
                    <span onClick={clear} className='font-dmSansMedium underline cursor-pointer text-primary-400 text-17'>Reset Signature</span>
                </div>}
                {sigTouchedAndError && (
                    <Alert boldLabel={false} label="You must sign before submitting." />
                )}
                <div className={'w-full'}>
                    <Button
                        testId="button-review-submit"
                        disabled={completeLoading}
                        loading={completeLoading}
                        showEndIcon
                        className="review__submit"
                        sx={{ mt: '5px', width: '100%', boxShadow: '0px 4px 7.1px 0px #0000000F' }}
                        onClick={() => {
                            //. formikControl.handleSubmit();
                            onSubmit?.()
                            setTimeout(scrollToFirstError, 200);
                        }}
                        fontClassName="font-bold"
                    >
                        Sign & Submit
                    </Button>
                </div>

                <div className="flex flex-wrap items-center justify-center gap-3 lg:gap-4 mt-[15px] ">
                    <img src={HippacIcon} alt="" className="h-12 max-w-25" />
                    <img src={EncryptionIcon} alt="" className="h-12 max-w-25" />
                </div>
            </div>

            <div className="shadow-appNavbar"></div>
        </Box>
    )
}

export default memo(ReviewSubmit);